import {
  useCallback, useEffect, useRef, useState,
} from "react";
import { useTransferProgress } from "@/features/employeeTransfer/hooks/useTransferProgress";
import {
  validateTransfer,
  requestOtp,
  verifyOtp,
  refuseTransfer,
  requestResend,
  extractErrorMessage,
  extractErrorStatus,
  extractErrorResendable,
  extractErrorKind,
} from "@/features/employeeTransfer/services/employeeTransfer.service";
import type {
  TransferRequest,
  WizardStep,
  ErrorKind,
  PendingAction,
} from "@/features/employeeTransfer/types/employeeTransfer.types";

const VALID_ERROR_KINDS: readonly ErrorKind[] = ["invalid", "expired", "lockout", "network", "rate_limit_exceeded"];

const toSafeErrorKind = (value: string | undefined): ErrorKind | undefined =>
  value !== undefined && VALID_ERROR_KINDS.includes(value as ErrorKind) ? (value as ErrorKind) : undefined;

type WizardState = {
  step: WizardStep
  pendingAction: PendingAction
  transferRequest: TransferRequest | null
  challengeId: string | null
  phoneHint: string | null
  errorKind: ErrorKind | null
  errorMessage: string | null
  isLoading: boolean
  isMutating: boolean
  otpError: string | null
  inlineError: string | null
  isResendable: boolean
  resendRequested: boolean
  percent: number
};

const INITIAL_STATE: WizardState = {
  step: "info",
  pendingAction: "confirm",
  transferRequest: null,
  challengeId: null,
  phoneHint: null,
  errorKind: null,
  errorMessage: null,
  isLoading: true,
  isMutating: false,
  otpError: null,
  inlineError: null,
  isResendable: false,
  resendRequested: false,
  percent: 0,
};

const STATUS_TO_ERROR_KIND: Record<number, ErrorKind> = {
  404: "invalid",
  410: "expired",
};

export const useEmployeeTransferWizard = (token: string) => {
  const [state, setState] = useState<WizardState>(INITIAL_STATE);
  const challengeIdRef = useRef<string | null>(null);
  const isMutatingRef = useRef(state.isMutating);
  isMutatingRef.current = state.isMutating;
  const pendingActionRef = useRef<PendingAction>(state.pendingAction);
  pendingActionRef.current = state.pendingAction;

  const updateState = useCallback((patch: Partial<WizardState>) => {
    setState((previous) => ({ ...previous, ...patch }));
  }, []);

  useEffect(() => {
    if (!token) {
      updateState({
        step: "error", errorKind: "invalid", errorMessage: "Lien invalide", isLoading: false,
      });
      return;
    }

    const controller = new AbortController();

    const fetchTransferInfo = async () => {
      try {
        const data = await validateTransfer(token, controller.signal);
        if (controller.signal.aborted) return;
        updateState({
          transferRequest: data.transfer_request,
          isLoading: false,
        });
      }
      catch (error: unknown) {
        if (controller.signal.aborted) return;
        const status = extractErrorStatus(error);
        const errorKindFromBody = extractErrorKind(error);
        const errorKindFromStatus = status !== undefined ? STATUS_TO_ERROR_KIND[status] : undefined;
        const errorKind = toSafeErrorKind(errorKindFromBody) ?? errorKindFromStatus ?? "network";
        const isResendable = errorKind === "expired" && extractErrorResendable(error);
        updateState({
          step: "error",
          errorKind,
          errorMessage: extractErrorMessage(error),
          isLoading: false,
          isResendable,
        });
      }
    };

    fetchTransferInfo();
    return () => controller.abort();
  }, [token, updateState]);

  const handleConfirm = useCallback(async () => {
    if (isMutatingRef.current) return;
    updateState({ isMutating: true, inlineError: null });
    try {
      const data = await requestOtp(token);
      challengeIdRef.current = data.challenge_id;
      updateState({
        step: "otp",
        pendingAction: "confirm",
        challengeId: data.challenge_id,
        phoneHint: data.phone_hint,
        isMutating: false,
      });
    }
    catch (error: unknown) {
      const status = extractErrorStatus(error);
      if (status === 422) {
        updateState({
          inlineError: "Aucun numéro de téléphone n'est associé à votre compte. Contactez votre CSE pour mettre à jour vos informations.",
          isMutating: false,
        });
      }
      else if (status === 429) {
        updateState({
          inlineError: "Trop de tentatives d'envoi. Veuillez réessayer dans quelques minutes.",
          isMutating: false,
        });
      }
      else {
        updateState({
          inlineError: extractErrorMessage(error),
          isMutating: false,
        });
      }
    }
  }, [token, updateState]);

  const handleVerifyOtp = useCallback(async (code: string) => {
    const currentChallengeId = challengeIdRef.current;
    if (!currentChallengeId) return;

    const action = pendingActionRef.current;
    updateState({ isMutating: true, otpError: null });

    try {
      if (action === "refuse") {
        await refuseTransfer(token, { challenge_id: currentChallengeId, code });
        updateState({ step: "refused", isMutating: false });
      }
      else {
        await verifyOtp(token, { challenge_id: currentChallengeId, code });
        updateState({ step: "processing", isMutating: true, percent: 0 });
      }
    }
    catch (error: unknown) {
      const status = extractErrorStatus(error);
      if (status === 429) {
        updateState({
          step: "error",
          errorKind: "lockout",
          errorMessage: "Trop de tentatives de vérification. Pour votre sécurité, veuillez réessayer dans 15 minutes ou contactez votre support.",
          isMutating: false,
        });
      }
      else if (action === "refuse" && status !== undefined && STATUS_TO_ERROR_KIND[status]) {
        updateState({
          step: "error",
          errorKind: STATUS_TO_ERROR_KIND[status],
          errorMessage: extractErrorMessage(error),
          isMutating: false,
        });
      }
      else {
        updateState({
          otpError: "Code incorrect, veuillez réessayer.",
          isMutating: false,
        });
      }
    }
  }, [token, updateState]);

  const handleResendOtp = useCallback(async () => {
    if (isMutatingRef.current) return;
    updateState({ isMutating: true });
    try {
      const data = await requestOtp(token);
      challengeIdRef.current = data.challenge_id;
      updateState({
        challengeId: data.challenge_id,
        phoneHint: data.phone_hint,
        isMutating: false,
        otpError: null,
      });
    }
    catch (error: unknown) {
      updateState({
        otpError: extractErrorMessage(error),
        isMutating: false,
      });
    }
  }, [token, updateState]);

  const handleRefuse = useCallback(async () => {
    if (isMutatingRef.current) return;
    updateState({ isMutating: true, inlineError: null });
    try {
      const data = await requestOtp(token);
      challengeIdRef.current = data.challenge_id;
      updateState({
        step: "otp",
        pendingAction: "refuse",
        challengeId: data.challenge_id,
        phoneHint: data.phone_hint,
        isMutating: false,
      });
    }
    catch (error: unknown) {
      const status = extractErrorStatus(error);
      if (status === 422) {
        updateState({
          inlineError: "Aucun numéro de téléphone n'est associé à votre compte. Contactez votre CSE pour mettre à jour vos informations.",
          isMutating: false,
        });
      }
      else if (status === 429) {
        updateState({
          inlineError: "Trop de tentatives d'envoi. Veuillez réessayer dans quelques minutes.",
          isMutating: false,
        });
      }
      else {
        updateState({
          inlineError: extractErrorMessage(error),
          isMutating: false,
        });
      }
    }
  }, [token, updateState]);

  const handleResendEmail = useCallback(async () => {
    if (isMutatingRef.current) return;
    updateState({ isMutating: true });
    try {
      await requestResend(token);
      updateState({ resendRequested: true, isMutating: false });
    }
    catch (error: unknown) {
      const errorKindFromBody = extractErrorKind(error);
      const errorKind = toSafeErrorKind(errorKindFromBody) ?? "network";
      updateState({
        errorKind,
        errorMessage: extractErrorMessage(error),
        isMutating: false,
      });
    }
  }, [token, updateState]);

  const clearInlineError = useCallback(() => {
    updateState({ inlineError: null });
  }, [updateState]);

  const handleTransferComplete = useCallback(() => {
    updateState({ step: "success", isMutating: false });
  }, [updateState]);

  const handleTransferFailed = useCallback(() => {
    updateState({ step: "failed", isMutating: false });
  }, [updateState]);

  const handleTransferProgress = useCallback((percent: number) => {
    updateState({ percent });
  }, [updateState]);

  useTransferProgress({
    token: state.step === "processing" ? token : null,
    onComplete: handleTransferComplete,
    onFailed: handleTransferFailed,
    onProgress: handleTransferProgress,
  });

  return {
    ...state,
    handleConfirm,
    handleVerifyOtp,
    handleResendOtp,
    handleRefuse,
    handleResendEmail,
    clearInlineError,
  };
};
